import {customElement, property, queryAll, state} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {PageScrollbar} from "../../../../common/pageScrollbar";
import {resolve} from "../../../../container";
import type {EopNavigationFlyoutContent} from "./navigationFlyoutContent";
import {eventOccurredOutsideAll, focusElementOnNextTabPress, installEscapeHandlerOn, onTabPressAfter, prepareEvents} from "../../../../common/utils/events";
import {GLOBAL} from "../../../../common/globals";
import {NavigationLevel1Item, NavigationRootItem, NavModel} from "../common/navModel";
import {Resolution} from "../../../../common/resolution";
import {NAVIGATION_TOGGLE_FLYOUT_EVENT, type NavigationToggleFlyoutEvent, type NavigationToggleFlyoutEventPayload} from "./desktopHeaderEvents";
import type {EopNavigationFlyoutToggle} from "./navigationFlyoutToggle";
import type {MetaLink} from "../common/navLinkTypes";
import {NAVIGATION_JUMP_INSIDE_PAGE_EVENT, type NavigationJumpInsidePageEvent} from "../common/headerEvents";
import {done, fresh, ManagingResources} from "../../../../common/lifetime";
import {PageFeatures} from "../../../elements/pageFeatures";
import {ClickTracking} from "../../../../tracking/clickTracking";
import {TrackingEventLocation, TrackingEventType} from "../../../../tracking/types";
import type {Maybe, Nullable} from "../../../../bootstrap/common/types";
import {Dictionary} from "../../../elements/dictionary";
import {trackingNavigationInteract} from "../../../../tracking/trackingAttributes";
import {UnLitElement} from "../../../../common/elements";

@customElement("eop-desktop-header-menu")
export class EopDesktopHeader extends ManagingResources(UnLitElement) {

    @property({attribute: "with-language-switch", type: Boolean})
    public withLanguageSwitch: boolean = false;
    @property({attribute: "transparent", type: Boolean})
    public transparent: boolean;
    @property({attribute: "opaque", type: Boolean})
    public opaque: boolean;
    @property({attribute: "reduced", type: Boolean})
    public reduced: boolean;
    @property({attribute: "full-navigation", type: Boolean})
    public fullNavigation: boolean = false;
    @property({reflect: true, type: Boolean})
    public open: boolean = false;
    @property({reflect: true, type: Boolean})
    public closedInstantly: boolean = false;

    @state()
    private navRoot: NavigationRootItem;
    @state()
    private metaLinks: MetaLink[] = [];
    @state()
    public currentNavId?: string;

    @queryAll("eop-navigation-flyout-toggle")
    private flyoutToggles: NodeListOf<EopNavigationFlyoutToggle>;
    @queryAll("eop-navigation-flyout-content")
    private flyoutContents: NodeListOf<EopNavigationFlyoutContent>;

    private backdrop: Nullable<Element>;
    private switchableScheme: boolean = false;
    private dictionary: Dictionary;

    public constructor(
        private pageScrollbar: PageScrollbar = resolve(PageScrollbar),
        private resolution: Resolution = resolve(Resolution),
        private tracking: ClickTracking = resolve(ClickTracking),
        private pageFeatures: PageFeatures = resolve(PageFeatures),
        private navModel: NavModel = resolve(NavModel)
    ) {
        super();
        this.metaLinks = this.navModel.getMetaLinks();
        this.navRoot = this.navModel.makeNavRoot();

        this.resolution.onWindowResize(() => {
            this.updateSelectedFlyoutContent();
        }, this);
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);

        this.switchableScheme = GLOBAL.bodyElement().classList.contains("switchable-scheme");
        this.backdrop = this.pageFeatures.findFirst(".header-desktop-menu-backdrop");

        this.addEventListener(NAVIGATION_TOGGLE_FLYOUT_EVENT, (event: Event) => {
            const toggled = (event as NavigationToggleFlyoutEvent).detail;
            this.toggleOpen(toggled);
        });

        this.addEventListener(NAVIGATION_JUMP_INSIDE_PAGE_EVENT, (event: Event) => {
            const targetHref = (event as NavigationJumpInsidePageEvent).detail;
            this.navRoot.activateFor(this.navModel.getPathHierarchy(), targetHref);
            this.navRoot = NavigationRootItem.copyFrom(this.navRoot);
            this.closeFlyoutInstantly();
        });

        installEscapeHandlerOn(this, event => this.closeFlyoutOnOutsideAction(event));
    }

    public render(): TemplateResult {
        return html`
            ${this.renderMetaAndSectorNavigation()}
            ${this.renderMainNavigation()}
        `;
    }

    private shouldRenderSearchLink(): boolean {
        return !!this.navModel.getActiveSector()?.searchHref || !!this.navModel.getSearchHref();
    }

    private renderMetaAndSectorNavigation(): Nullable<TemplateResult> {
        if (!this.shouldRenderSearchLink()
            && this.metaLinks.isEmpty()
            && this.navRoot.countSectors() <= 1
            && !this.withLanguageSwitch
            && !this.switchableScheme) {
            return null;
        }

        return html`
            <div class="meta-navigation-spacer">
                <div class="meta-navigation-container">
                    <div class="meta-navigation">
                        ${(this.renderSectors())}
                        ${(this.renderMetaLinks())}
                        ${this.renderSearchLink()}
                        ${this.renderColorSchemeToggle()}
                        ${this.renderLanguageSwitch()}
                    </div>
                </div>
            </div>
        `;
    }

    private renderSectors(): TemplateResult[] {
        return this.navRoot.getSectors()
            .map(sectorLink => this.renderSectorLink(sectorLink));
    }

    private renderSectorLink(sectorLink: NavigationLevel1Item): TemplateResult {
        const classes = `sector-link${sectorLink.active ? " highlighted" : ""}`;
        return html`
            <a class=${classes}
               href=${sectorLink.href}
               target=${sectorLink.target}
               title=${sectorLink.label}
               ${trackingNavigationInteract(sectorLink.label, sectorLink.href)}
            >${sectorLink.label}</a>`;
    }

    private renderMetaLinks(): TemplateResult[] {
        return this.metaLinks.map(metaLink => this.renderMetaLink(metaLink));
    }

    private renderMetaLink(metaLink: MetaLink): TemplateResult {
        return html`
            <a href=${metaLink.href}
               target=${metaLink.target}
               title=${metaLink.label}
               class="meta-link"
               ${trackingNavigationInteract(metaLink.label, metaLink.href)}
            >${metaLink.label}</a>`;
    }

    private renderSearchLink(): TemplateResult {
        return html`
            <eop-navigation-search-link></eop-navigation-search-link>`;
    }

    private renderColorSchemeToggle(): Nullable<TemplateResult> {
        if (!this.switchableScheme) {
            return null;
        }

        return html`
            <eop-color-scheme-toggle></eop-color-scheme-toggle>`;
    }

    private renderLanguageSwitch(): Nullable<TemplateResult> {
        if (!this.withLanguageSwitch) {
            return null;
        }

        return html`
            <eop-desktop-language-switch></eop-desktop-language-switch>`;
    }

    private renderMainNavigation(): Nullable<TemplateResult> {
        const activeSector = this.navRoot.getActiveSector();
        if (!activeSector?.hasSubItems()) {
            return null;
        }

        return html`
            <div class="main-navigation-spacer">
                <div class="main-navigation-container">
                    <nav class="main-navigation" aria-label=${this.dictionary.translate("MSG_MAIN_NAVIGATION")}>
                        ${(this.renderFlyoutToggle(activeSector))}
                    </nav>
                </div>
            </div>
            <div class="desktop-flyout-viewport">
                <div class="desktop-flyout-area">
                    <div class="desktop-flyout-container">
                        <div class="desktop-flyout-panel">
                            ${this.renderFlyoutContents(activeSector)}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    private renderFlyoutToggle(activeSector: NavigationLevel1Item): Nullable<TemplateResult[]> {
        const numberOfMainNavigationItems = activeSector.getSubItems().length;
        return activeSector.getSubItems().map((item, index) => html`
            <eop-navigation-flyout-toggle
                    ?light-link-highlight=${this.transparent && !this.opaque}
                    .model=${item}
                    ?last-item=${index === numberOfMainNavigationItems - 1}
            >
            </eop-navigation-flyout-toggle>
        `);
    }

    private renderFlyoutContents(activeSector: NavigationLevel1Item): TemplateResult [] {
        return activeSector.getSubItems()
            .filter(item => item.hasSubItems())
            .map(item => html`
                <eop-navigation-flyout-content
                        .model=${item}
                        .closedInstantly=${this.closedInstantly}>
                </eop-navigation-flyout-content>
            `);
    }

    private toggleOpen(navItem: NavigationToggleFlyoutEventPayload): void {
        if (this.currentNavId === navItem.id) {
            this.closeFlyout();
        } else if (this.open) {
            this.selectFlyout(navItem);
        } else {
            this.openFlyout(navItem);
        }
    }

    private openFlyout(navItem: NavigationToggleFlyoutEventPayload): void {
        const lifetime = fresh(this, "flyout");
        this.open = true;
        this.closedInstantly = false;
        this.backdrop?.classList.add("visible");

        this.pageScrollbar.disablePageScrollability();
        this.selectFlyout(navItem);
        prepareEvents(GLOBAL.bodyElement())
            .boundTo(lifetime)
            .on("click", e => this.closeOnClickOutside(e));
    }

    private closeOnClickOutside(event: Event): void {
        const flyoutPanel = this.renderRoot.querySelector(".desktop-flyout-panel")!;
        if (eventOccurredOutsideAll(event, flyoutPanel, ...this.flyoutToggles)) {
            this.closeFlyoutOnOutsideAction(event);
        }
    }

    private closeFlyout(): void {
        this.open = false;
        this.backdrop?.classList.remove("visible");

        this.pageScrollbar.enablePageScrollability();
        this.deselectAllFlyouts();
        this.style.setProperty("--flyout-height", `0px`);
        done(this, "flyout");
    }

    private closeFlyoutInstantly(): void {
        this.closeFlyout();
        this.closedInstantly = true;
    }

    private closeFlyoutOnOutsideAction(event: Event): void {
        if (this.open) {
            this.closeFlyout();
            this.tracking.handleDislocatedNavigationInteract(event,
                "desktop-flyout-toggle", TrackingEventLocation.HEADER, "header", TrackingEventType.CLOSE);
        }
    }

    private selectFlyout(navItem: NavigationToggleFlyoutEventPayload): void {
        this.deselectAllFlyouts();
        this.currentNavId = navItem.id;
        let nextFlyoutToggle: Maybe<HTMLElement>;
        this.flyoutToggles.forEach((flyoutToggle, index) => {
            if (flyoutToggle.hasId(navItem.id)) {
                flyoutToggle.selected = true;
                if (this.flyoutToggles[index + 1]) {
                    nextFlyoutToggle = this.flyoutToggles[index + 1].focusableElement();
                }
            }
        });
        this.flyoutContents.forEach(flyoutContent => {
            if (flyoutContent.hasId(navItem.id)) {
                flyoutContent.selected = true;
                this.updateFlyoutContent(flyoutContent);
                const firstLink = flyoutContent.getFirstLink();
                if (firstLink) {
                    focusElementOnNextTabPress(firstLink);
                }
                const lastLink = flyoutContent.getLastLink();
                if (lastLink) {
                    onTabPressAfter(lastLink, () => this.closeFlyout(), nextFlyoutToggle);
                }
            }
        });
    }

    private deselectAllFlyouts(): void {
        this.currentNavId = undefined;
        this.flyoutToggles.forEach(flyoutToggle => flyoutToggle.selected = false);
        this.flyoutContents.forEach(flyoutContent => flyoutContent.selected = false);
    }

    private updateSelectedFlyoutContent(): void {
        this.flyoutContents.forEach(flyoutContent => {
            if (flyoutContent.selected) {
                this.updateFlyoutContent(flyoutContent);
            }
        });
    }

    private updateFlyoutContent(flyoutContent: EopNavigationFlyoutContent): void {
        this.style.setProperty("--flyout-height", `${(flyoutContent.clientHeight)}px`);
        this.style.setProperty("--arrow-up-offset", `${this.calculateArrowUpOffsetX()}px`);
    }

    private calculateArrowUpOffsetX(): number {
        const panel = this.renderRoot.querySelector(".desktop-flyout-panel")!;
        const panelRect = panel.getBoundingClientRect();

        const selectedFlyoutToggle = Array.from(this.flyoutToggles)
            .find(toggle => toggle.selected)!;
        const toggleRect = selectedFlyoutToggle.getBoundingClientRect();

        const flyoutToggleCenterX = toggleRect.x + (toggleRect.width / 2);
        return flyoutToggleCenterX - panelRect.x - 2;
    }
}